import { Close } from "@mui/icons-material";
import {
  Alert,
  Autocomplete,
  Collapse,
  FormControl,
  IconButton,
  Modal,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import {
  FormEvent,
  SyntheticEvent,
  useCallback,
  useMemo,
  useState,
} from "react";
import { useGeneratePatientVisitUrlMutation } from "src/api/services/onlineVisitsApi";
import { useGetProductsQuery } from "src/api/services/productsApi";
import { MedicationForm, MedicationFormEnums, Product } from "src/api/types";
import AlertWithCopy from "src/components/AlertWithCopy";
import LoadingButton from "src/components/Buttons/LoadingButton";
import { useSentryLogger } from "src/hooks/useSentryLogger";
import { desktopSx } from "src/utils/styles/modalStyles";

interface Props {
  patientId: number | null;
  open: boolean;
  onClose: () => void;
}

const CreateVisitModal = ({ onClose, open, patientId }: Props) => {
  const { data: products = [] } = useGetProductsQuery(
    { from: 0, to: 100 },
    {
      selectFromResult: ({ data }) => {
        return { data: data?.data ?? [] };
      },
    },
  );
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [selectedForm, setSelectedForm] = useState<MedicationForm | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [alert, setAlert] = useState<boolean>(false);
  const [message, setMessage] = useState<string>("");
  const [link, setLink] = useState<string>("");
  const [generateLink] = useGeneratePatientVisitUrlMutation();
  const errorLog = useSentryLogger("error");

  const filteredMedicationForms = useMemo(() => {
    if (selectedProduct == null) return MedicationFormEnums;

    return MedicationFormEnums.filter((form) =>
      selectedProduct.name === "Male Hair Loss"
        ? form !== "dropper"
        : form !== "topical",
    );
  }, [selectedProduct]);

  const handleProduct = useCallback(
    (_: SyntheticEvent<Element | Event>, value: Product | null) => {
      setSelectedProduct(value);
    },
    [],
  );

  const handleMedicationForm = useCallback(
    (_: SyntheticEvent<Element | Event>, value: MedicationForm | null) => {
      setSelectedForm(value);
    },
    [],
  );

  const handleClose = useCallback(() => {
    setAlert(false);
    setMessage("");
    setSelectedProduct(null);
    onClose();
  }, [onClose]);

  const hasFormSelection = useMemo(() => {
    if (selectedProduct == null) return;

    return selectedProduct.category.name === "Hair Loss";
  }, [selectedProduct]);

  const handleGenerate = useCallback(
    async (e: FormEvent<HTMLFormElement>) => {
      e.preventDefault();

      if (selectedProduct == null) {
        setMessage("Please make sure a product is selected");
        return;
      }

      if (patientId == null) {
        setMessage("Please make sure that the patient has a valid ID");
        return;
      }

      setLoading(true);

      try {
        const link = await generateLink({
          product: selectedProduct.slug,
          patient_id: patientId,
          visit_type: "Upsell",
          metadata: {
            form: selectedForm ?? undefined,
          },
        }).unwrap();

        setAlert(true);
        setLink(link);
      } catch (error) {
        errorLog("Unable to generate visit link", error);
        setMessage(
          "Oops, looks like something went wrong and the visit was unable to be generated",
        );
      } finally {
        setLoading(false);
      }
    },
    [errorLog, generateLink, patientId, selectedForm, selectedProduct],
  );

  return (
    <Modal open={open}>
      <Stack
        sx={desktopSx}
        gap={3}
        component={"form"}
        onSubmit={handleGenerate}
      >
        <Stack
          direction={"row"}
          justifyContent={"space-between"}
          alignItems={"center"}
        >
          <Typography variant="h5" fontWeight={700}>
            Create Upsell Visit
          </Typography>
          <IconButton onClick={handleClose} sx={{ p: 0 }}>
            <Close fontSize="medium" />
          </IconButton>
        </Stack>
        <FormControl>
          <Autocomplete
            options={products}
            getOptionLabel={(p) => p.name}
            renderInput={(params) => (
              <TextField {...params} label="Product" required />
            )}
            value={selectedProduct}
            onChange={handleProduct}
          />
        </FormControl>
        <Collapse in={hasFormSelection} unmountOnExit>
          <FormControl fullWidth>
            <Autocomplete
              options={filteredMedicationForms}
              getOptionLabel={(form) => form}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Form"
                  required
                  helperText="Select medication form to generate the correct link"
                />
              )}
              value={selectedForm}
              onChange={handleMedicationForm}
            />
          </FormControl>
        </Collapse>
        {alert ? (
          <AlertWithCopy
            text={`Visit generated successfully! Please copy and send it to the patient: ${link}`}
            copyText={link}
          />
        ) : (
          <LoadingButton
            type="submit"
            loading={loading}
            fullWidth
            disabled={!selectedProduct}
          >
            Generate link
          </LoadingButton>
        )}
        {message ? <Alert severity={"error"}>{message}</Alert> : null}
      </Stack>
    </Modal>
  );
};

export default CreateVisitModal;
